import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Megaphone, CheckCircle2, ArrowRight, Clock, AlertCircle, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase3Vermarktung = () => {
  const vermarktungsSchritte = [
    { titel: 'Professionelles Exposé erstellen', details: '20-30 Seiten, Fotos, Grundrisse, Kennzahlen, Standortanalyse, Mieterinfo', pflicht: true },
    { titel: 'Teaser-Dokument (1-Seiter)', details: 'Für erste Ansprache ohne sensible Details (Off-Market)', pflicht: true },
    { titel: 'Zielgruppen-Definition', details: 'Welcher Käufertyp passt? (Family Office, Pensionsfonds, Private, Bestandshalter)', pflicht: true },
    { titel: 'Käufer-Datenbank aktivieren', details: '150-300 relevante Adressen aus eigenem Netzwerk kontaktieren', pflicht: true },
    { titel: 'Off-Market-Phase', details: '2-4 Wochen diskrete Ansprache ohne öffentliche Ausschreibung', pflicht: false },
    { titel: 'Online-Portale (falls nötig)', details: 'ImmoScout24, Immobilien-Zeitung, PropertyEU (nur wenn Off-Market nicht erfolgreich)', pflicht: false },
    { titel: 'Besichtigungen koordinieren', details: 'Max. 3-5 Termine mit vorqualifizierten Interessenten, Mieter diskret informieren', pflicht: true },
    { titel: 'Datenraum einrichten', details: 'Digitaler Datenraum (Drooms, DocuWare) für Due Diligence-Unterlagen', pflicht: true },
    { titel: 'NDA (Vertraulichkeitsvereinbarung)', details: 'Alle Interessenten unterschreiben vor Erhalt des vollständigen Exposés', pflicht: true },
    { titel: 'Erstkontakt-Screening', details: 'Finanzierungsnachweis, Kaufabsicht, Erfahrung mit Assetklasse prüfen', pflicht: true }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer', aufgaben: ['Besichtigungstermine ermöglichen (Absprache mit Mieter)', 'Ansprechpartner für Käuferfragen benennen', 'Entscheidung: Off-Market oder öffentlich?', 'Mieter frühzeitig (diskret) informieren'] },
    { wer: 'RetailInvest', aufgaben: ['Exposé professionell gestalten (Grafik, Fotos, Texte)', 'Käufer-Longlist erstellen (200+ Adressen)', 'Erstkontakte führen und vorqualifizieren', 'Besichtigungen organisieren und begleiten', 'Datenraum aufsetzen und pflegen', 'Interessenten-Tracking (wer, wann, Feedback)'] },
    { wer: 'Fotograf (extern)', aufgaben: ['Professionelle Außen- und Innenaufnahmen', 'Drohnenaufnahmen (Luftbild Standort)', 'Kosten: € 800-1.500'] }
  ];

  const typischeProbleme = [
    { problem: 'Mieter erfährt von Verkauf und fürchtet Veränderungen (Eigenbedarfskündigung?)', loesung: 'Proaktiv kommunizieren: "Mietvertrag bleibt bestehen", "Neuer Eigentümer sucht langfristige Mieteinnahmen". Mieter hat gesetzliches Vorkaufsrecht (2 Monate Frist).' },
    { problem: 'Zu wenig qualifizierte Anfragen (nur 2-3 Interessenten)', loesung: 'Preis zu hoch? Exposé zu schwach? Zielgruppe falsch definiert? → Strategie-Check nach 4 Wochen. Ggf. Preis anpassen oder öffentlich ausschreiben.' },
    { problem: 'Käufer verlangen Exklusivität („Wir wollen 4 Wochen allein prüfen")', loesung: 'Nur gegen Anzahlung (1-3% des Kaufpreises, verfallbar). Sonst parallele Prozesse fahren, um Druck zu halten.' },
    { problem: 'Konkurrenz-Objekt kommt zeitgleich auf den Markt (ähnlicher Mieter, 5 km entfernt)', loesung: 'USPs herausarbeiten: Längerer Mietvertrag? Bessere Lage? Modernere Immobilie? Preis anpassen oder schneller sein (First Mover Advantage).' }
  ];

  const kaeuferTypen = [
    { typ: 'Family Offices', merkmale: 'Langfristig, 100% Eigenkapital, schnelle Entscheidung, bevorzugen Off-Market', ticketsize: '€ 2-10 Mio.' },
    { typ: 'Institutionelle (Pensionsfonds, Versicherungen)', merkmale: 'Strenges Reporting, 60-70% Fremdkapital, langsame Gremien-Entscheidung, brauchen Gutachten', ticketsize: '€ 5-50 Mio.' },
    { typ: 'Private Investoren', merkmale: 'Steuermotiviert, emotionaler, teilweise erstmaliger Kauf, brauchen Beratung', ticketsize: '€ 0,5-3 Mio.' },
    { typ: 'Bestandshalter (Portfolios)', merkmale: 'Suchen Cluster (mehrere Objekte einer Region/Assetklasse), professionell, schnell', ticketsize: '€ 3-20 Mio.' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 3: Käuferansprache & Vermarktung – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 3: Exposé-Erstellung, Off-Market vs. öffentliche Vermarktung, Käufertypen, Besichtigungen." url="/ablauf/phase-3-vermarktung" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
              <span className="text-4xl font-bold text-white">03</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Käuferansprache & Vermarktung</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 4-8 Wochen</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Warum ist diese Phase so wichtig?</h2>
            <p className="text-lg mb-4 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Mehr qualifizierte Käufer = höherer Preis.</strong> Während bei Wohnimmobilien Portale dominieren, läuft bei Gewerbeimmobilien über 60% der Transaktionen "Off-Market" – also diskret über persönliche Netzwerke. Der richtige Käufer ist oft derjenige, der noch gar nicht aktiv sucht, sondern angesprochen werden muss.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              Ein professionelles Exposé ist Ihre Visitenkarte. Es entscheidet in 30 Sekunden, ob ein Investor weiterliest oder ablegt. <strong>Schlechte Fotos, unklare Kennzahlen oder fehlendes Storytelling kosten Sie 5-10% Verkaufspreis.</strong>
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Vermarktungs-Checkliste</h2>
            <div className="space-y-4">
              {vermarktungsSchritte.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: s.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{s.titel}</h3>
                      {s.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{s.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Käufertypen & Ansprache-Strategie</h2>
            <div className="space-y-6">
              {kaeuferTypen.map((k, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold" style={{ color: 'var(--navy-800)' }}>{k.typ}</h3>
                    <span className="text-sm font-bold px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                      {k.ticketsize}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--gray-700)' }}>{k.merkmale}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Expertentipp: Off-Market vs. Öffentlich</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>Off-Market (empfohlen)</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>✓ Diskretion: Mieter/Konkurrenz erfährt nichts</li>
                  <li>✓ Qualität statt Quantität: Nur seriöse Käufer</li>
                  <li>✓ Schneller: 4-8 Wochen bis Angebot</li>
                  <li>✓ Höherer Preis: Kein "Ladenhüter"-Stigma</li>
                  <li>✓ Weniger Aufwand: Max. 5 Besichtigungen</li>
                </ul>
              </div>
              <div className="p-6 bg-yellow-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--warning-700)' }}>Öffentlich (wenn Off-Market scheitert)</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>○ Reichweite: 50-200 Anfragen (viel Schaulaufen)</li>
                  <li>○ Transparenz: Alle sehen, wie lange Objekt schon angeboten wird</li>
                  <li>○ Preisdruck: Nach 8 Wochen ohne Verkauf → Rabatt erwartet</li>
                  <li>○ Mehr Arbeit: 15-30 Besichtigungen, viele unqualifizierte Anfragen</li>
                  <li>○ Sinnvoll bei: Kleinen Objekten (&lt; € 2 Mio.) oder exotischen Lagen</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf/phase-2-bewertung" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zu Phase 2
              </Button>
            </Link>
            <Link to="/ablauf/phase-4-verhandlung" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Weiter zu Phase 4: Verhandlung
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase3Vermarktung;
